import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  level: LogLevel;
  service: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly isDevelopment = !environment.production;
  private logLevel: LogLevel = this.isDevelopment ? 'debug' : 'warn';
  private readonly maxEntries = 1000;
  private entries: LogEntry[] = [];

  private readonly levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  debug(service: string, message: string, data?: Record<string, unknown>): void {
    this.log('debug', service, message, data);
  }

  info(service: string, message: string, data?: Record<string, unknown>): void {
    this.log('info', service, message, data);
  }

  warn(service: string, message: string, data?: Record<string, unknown>): void {
    this.log('warn', service, message, data);
  }

  error(service: string, message: string, data?: Record<string, unknown>): void {
    this.log('error', service, message, data);
  }

  private log(level: LogLevel, service: string, message: string, data?: Record<string, unknown>): void {
    if (this.levelPriority[level] < this.levelPriority[this.logLevel]) {
      return;
    }

    const entry: LogEntry = {
      level,
      service,
      message,
      data: this.sanitizeData(data),
      timestamp: new Date().toISOString(),
    };

    this.entries.push(entry);
    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }

    const consoleMethod = level === 'debug' ? 'log' : level;
    const prefix = `[${entry.timestamp}] [${level.toUpperCase()}] [${service}]`;
    
    if (data) {
      (console[consoleMethod] as Function)(prefix, message, data);
    } else {
      (console[consoleMethod] as Function)(prefix, message);
    }
  }

  private sanitizeData(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!data) return undefined;
    
    const sensitiveKeys = ['token', 'password', 'refreshToken', 'accessToken', 'authorization', 'auth', 'secret', 'key'];
    const sanitized: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(k => lowerKey.includes(k))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeData(value as Record<string, unknown>);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }

  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  clear(): void {
    this.entries = [];
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
}